/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useContext, type ReactNode } from 'react';
import type { SnippetsSectionProps } from '../Snippets/types';
import { Code } from './Code';
import { HighlightCode } from '../../HighlightCode/HighlightCode';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import { Text } from './Text';
import { getFilename } from '../../../utils/getFilename/getFilename';
import { SECTION_NAME_JS } from '../../../constants/snippetsSectionNames';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';

export function VueMainJs( {
	isLoading,
	number,
	onCopyEvent
}: SnippetsSectionProps ): ReactNode {
	const { selectedTechnology } = useContext( InstallationSettingsContext );
	const cssFilename = getFilename( { type: 'css', selectedTechnology } );
	const vueMainJsFilename = getFilename( { type: 'vueMainJs', selectedTechnology } );

	const mainJsCodeSnippet = [
		'import { createApp } from \'vue\'',
		'import App from \'./App.vue\'',
		`import './${ cssFilename! }'`,
		'',
		'createApp( App ).mount( \'#app\' );'
	].join( '\n' );

	return (
		<SkeletonLoader number={ number } sectionTitle={ SECTION_NAME_JS } isLoading={ isLoading }>
			<SnippetsSection headerCharacter={ number } title={ SECTION_NAME_JS }>
				<Text>
					Create a <Code>{ vueMainJsFilename }</Code> file, and paste there the code from below.
					Make sure it is created in the same directory as <Code>{ cssFilename }</Code>.
				</Text>
				<HighlightCode onCopyEvent={ onCopyEvent } lang={ 'js' } id={ 'snippet-vue-main-js' }>
					{ mainJsCodeSnippet }
				</HighlightCode>
			</SnippetsSection>
		</SkeletonLoader>
	);
}
